import { useRef, useState } from 'react';
import type { Task, TaskAttachment, TaskNote } from '../types';
import {
  deleteAttachment as apiDeleteAttachment,
  uploadAttachment,
} from '../api/attachments';
import { getAttachmentUrl } from '../utils/apiClient';

interface Props {
  task: Task;
  notes: TaskNote[];
  onAdd: (body: string) => Promise<void>;
  onTaskReplaced?: (task: Task) => void;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const MAX_BYTES = 25 * 1024 * 1024;

export function NotesTimeline({ task, notes, onAdd, onTaskReplaced }: Props) {
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const composerFileRef = useRef<HTMLInputElement>(null);
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Always-current task ref so submit() can read the note list AFTER onAdd()'s
  // setState commits during the await. Without this, `task.notes` in the
  // post-await continuation is the pre-add snapshot captured by the closure.
  const taskRef = useRef(task);
  taskRef.current = task;

  function addToStaging(files: FileList | null) {
    if (!files || files.length === 0) return;
    setError(null);
    const accepted: File[] = [];
    for (const f of Array.from(files)) {
      if (f.size > MAX_BYTES) {
        setError(`${f.name} exceeds ${MAX_BYTES / (1024 * 1024)} MB limit`);
        continue;
      }
      accepted.push(f);
    }
    setStagedFiles((prev) => [...prev, ...accepted]);
    if (composerFileRef.current) composerFileRef.current.value = '';
  }

  function removeStaged(idx: number) {
    setStagedFiles((prev) => prev.filter((_, i) => i !== idx));
  }

  async function submit() {
    const body = draft.trim();
    // Allow note-less attachment uploads only if you typed *and* staged, or
    // typed alone. If nothing, skip.
    if (!body && stagedFiles.length === 0) return;
    if (busy) return;
    // The /notes endpoint requires a body; if files are staged without a
    // body, we tell the user rather than guess.
    if (!body) {
      setError('Add a note message before attaching files.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onAdd(body);
      setDraft('');

      if (stagedFiles.length > 0) {
        // Read the POST-add task off the ref — taskRef.current was mutated
        // during the parent's re-render between await points.
        const latest = taskRef.current;
        const latestNote = latest.notes[latest.notes.length - 1];
        const noteId = latestNote?.noteId;
        if (!noteId) {
          setError('Note saved, but could not locate it to attach files.');
        } else {
          let current: Task | null = null;
          for (const file of stagedFiles) {
            current = await uploadAttachment(latest.teamId, latest.taskId, file, {
              noteId,
            });
          }
          if (current && onTaskReplaced) onTaskReplaced(current);
        }
        setStagedFiles([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function downloadAttachment(noteId: string, att: TaskAttachment) {
    const key = att.id ?? att.filename;
    setDownloadingKey(`${noteId}:${key}`);
    try {
      const url = await getAttachmentUrl(task.teamId, task.taskId, key);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      setDownloadingKey(null);
    }
  }

  async function removeAttachment(att: TaskAttachment) {
    if (!att.id) return;
    if (!confirm(`Remove "${att.filename}"?`)) return;
    setDeletingId(att.id);
    try {
      const updated = await apiDeleteAttachment(task.teamId, task.taskId, att.id);
      if (onTaskReplaced) onTaskReplaced(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className="notes-timeline">
      <h3>Notes</h3>
      {notes.length === 0 ? (
        <p className="muted">No notes yet.</p>
      ) : (
        <ul className="notes-list">
          {notes.map((note) => (
            <li key={note.noteId}>
              <div className="note-meta">
                {new Date(note.createdAt).toLocaleString()}
              </div>
              <div className="note-body">{note.body}</div>
              {note.attachments && note.attachments.length > 0 && (
                <ul className="note-attachments">
                  {note.attachments.map((a) => {
                    const key = a.id ?? a.s3Key;
                    return (
                      <li key={key} className="note-attachment">
                        <button
                          type="button"
                          className="note-attachment-link"
                          onClick={() => void downloadAttachment(note.noteId, a)}
                          disabled={downloadingKey === `${note.noteId}:${a.id ?? a.filename}`}
                          title="Download"
                        >
                          📎 {a.filename}
                        </button>
                        <span className="note-attachment-size">{formatBytes(a.size)}</span>
                        {a.id && (
                          <button
                            type="button"
                            className="note-attachment-remove"
                            onClick={() => void removeAttachment(a)}
                            disabled={deletingId === a.id}
                            aria-label={`Remove ${a.filename}`}
                            title="Remove"
                          >
                            ×
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="note-composer">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Add a note — task will move to Pending"
          rows={3}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              void submit();
            }
          }}
        />
        {stagedFiles.length > 0 && (
          <ul className="note-composer-staged">
            {stagedFiles.map((f, i) => (
              <li key={`${f.name}-${i}`}>
                <span>📎 {f.name}</span>
                <span className="note-attachment-size">{formatBytes(f.size)}</span>
                <button
                  type="button"
                  className="note-attachment-remove"
                  onClick={() => removeStaged(i)}
                  aria-label={`Remove staged ${f.name}`}
                  disabled={busy}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
        {error && <div className="note-composer-error">{error}</div>}
        <div className="note-composer-actions">
          <button
            type="button"
            className="note-composer-attach"
            onClick={() => composerFileRef.current?.click()}
            disabled={busy}
            title="Attach files to this note"
          >
            📎 Attach
          </button>
          <input
            ref={composerFileRef}
            type="file"
            hidden
            multiple
            onChange={(e) => addToStaging(e.target.files)}
          />
          <button
            onClick={() => void submit()}
            disabled={busy || (!draft.trim() && stagedFiles.length === 0)}
          >
            {busy ? 'Adding…' : 'Add note'}
          </button>
        </div>
      </div>
    </div>
  );
}
